/**
 * src/lib/recaptchaEnterprise.js
 *
 * reCAPTCHA Enterprise Phase 1:
 * - Disabled or incomplete config = fail open / do nothing
 * - Enabled and fully configured but script/execute fails = throw and block submit
 *
 * Frontend token generation alone is not real protection.
 * Backend verification must be implemented separately.
 */

let scriptLoadPromise = null;

export const isRecaptchaEnterpriseReady = (config, action) => {
  return (
    String(config?.enabled) === 'true' &&
    !!String(config?.site_key || '').trim() &&
    !!String(config?.project_id || '').trim() &&
    !!String(action || '').trim()
  );
};

export const loadRecaptchaEnterprise = (siteKey) => {
  const cleanSiteKey = String(siteKey || '').trim();

  if (!cleanSiteKey) {
    return Promise.reject(new Error('RECAPTCHA_NOT_CONFIGURED'));
  }

  if (window.grecaptcha?.enterprise) {
    return Promise.resolve();
  }

  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  scriptLoadPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector(
      `script[src*="recaptcha/enterprise.js"]`
    );

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener(
        'error',
        () => reject(new Error('RECAPTCHA_SCRIPT_LOAD_FAILED')),
        { once: true }
      );
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(cleanSiteKey)}`;
    script.async = true;
    script.defer = true;

    script.onload = () => resolve();
    script.onerror = () => {
      scriptLoadPromise = null;
      reject(new Error('RECAPTCHA_SCRIPT_LOAD_FAILED'));
    };

    document.head.appendChild(script);
  });

  return scriptLoadPromise;
};

export const executeRecaptchaEnterprise = async (action, config) => {
  const cleanAction = String(action || '').trim();

  if (!isRecaptchaEnterpriseReady(config, cleanAction)) {
    return {
      enabled: false,
      status: 'RECAPTCHA_NOT_CONFIGURED',
      token: null,
      action: cleanAction
    };
  }

  const siteKey = String(config.site_key || '').trim();

  await loadRecaptchaEnterprise(siteKey);

  if (!window.grecaptcha?.enterprise) {
    throw new Error('RECAPTCHA_SCRIPT_LOAD_FAILED');
  }

  return new Promise((resolve, reject) => {
    window.grecaptcha.enterprise.ready(async () => {
      try {
        const token = await window.grecaptcha.enterprise.execute(siteKey, {
          action: cleanAction
        });

        if (!token) {
          reject(new Error('RECAPTCHA_TOKEN_MISSING'));
          return;
        }

        resolve({
          enabled: true,
          status: 'OK',
          token,
          action: cleanAction
        });
      } catch (err) {
        console.error('reCAPTCHA execution error:', err);
        reject(new Error('RECAPTCHA_EXECUTE_FAILED'));
      }
    });
  });
};